import React, { useRef, useEffect, useState } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';

// Google Maps Autocomplete Component
const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onPlaceSelect, 
  placeholder = "Start typing an address...",
  className = "",
  disabled = false
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Check if Google Maps API is loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      initAutocomplete();
    } else {
      // Load Google Maps API
      loadGoogleMapsScript();
    }

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  const loadGoogleMapsScript = () => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('Google Maps API key not configured');
      return;
    }

    // Check if script already exists
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Wait for it to load
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkLoaded);
          initAutocomplete();
        }
      }, 100);
      return;
    }

    setIsLoading(true);
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsLoading(false);
      initAutocomplete();
    };
    script.onerror = () => {
      setIsLoading(false);
      console.error('Failed to load Google Maps API');
    };
    document.head.appendChild(script);
  };

  const initAutocomplete = () => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: ['gb', 'ie', 'fr', 'de', 'es', 'it', 'nl', 'be'] }, // UK, Ireland, and major EU countries
      fields: ['address_components', 'formatted_address', 'geometry', 'name']
    });

    autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
    setIsLoaded(true);
  };

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    
    if (!place.address_components) {
      return;
    }

    // Parse address components
    const addressData = {
      formatted_address: place.formatted_address || '',
      street: '',
      city: '',
      postal_code: '',
      country: '',
      country_code: '',
      lat: place.geometry?.location?.lat(),
      lng: place.geometry?.location?.lng(),
    };

    // Extract address components
    let streetNumber = '';
    let route = '';

    place.address_components.forEach((component) => {
      const types = component.types;

      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality') || types.includes('postal_town')) {
        addressData.city = component.long_name;
      }
      if (types.includes('postal_code')) {
        addressData.postal_code = component.long_name;
      }
      if (types.includes('country')) {
        addressData.country = component.long_name;
        addressData.country_code = component.short_name;
      }
    });

    // Combine street number and route
    addressData.street = streetNumber ? `${streetNumber} ${route}` : route;

    // Callback with parsed data
    if (onPlaceSelect) {
      onPlaceSelect(addressData);
    }

    // Update input value
    if (onChange) {
      onChange(place.formatted_address || '');
    }
  };

  const handleInputChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        {isLoading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <MapPin size={18} />
        )}
      </div>
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`pl-10 ${className}`}
        disabled={disabled || isLoading}
        autoComplete="off"
      />
      {isLoaded && (
        <p className="text-xs text-gray-400 mt-1">
          Powered by Google Maps
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
